import React, { useEffect, useRef, useState } from 'react';
import { shopAPI } from '../../utils/api';
import '../../assets/Shops.css';
import '../../App.css';
import { loadGoogleMaps } from '../../utils/googleMaps';

const DEFAULT_CENTER = { lat: 25.285447, lng: 51.531040 };

export const ManualAddStore: React.FC<{ onAdd: () => void }> = ({ onAdd }) => {
    const [name, setName] = useState('');
    const [latitude, setLatitude] = useState<number | ''>(25.2854);
    const [longitude, setLongitude] = useState<number | ''>(51.5310);
    const [phone, setPhone] = useState('');
    const [productType, setProductType] = useState('');
    const [crNumber, setCrNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [mapsReady, setMapsReady] = useState(false);
    const [mapsMessage, setMapsMessage] = useState<string | null>(null);

    const autocompleteInputRef = useRef<HTMLInputElement | null>(null);
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const mapClickListenerRef = useRef<any>(null);
    const geocoderRef = useRef<any>(null);
    const autocompleteRef = useRef<any>(null);

    useEffect(() => {
        let isMounted = true;
        setMapsMessage(null);
        setMapsReady(false);

        loadGoogleMaps({ libraries: ['places'] })
            .then(() => {
                if (!isMounted) return;
                setMapsReady(true);
                setMapsMessage(null);
            })
            .catch((error: Error) => {
                if (!isMounted) return;
                setMapsReady(false);
                setMapsMessage(error.message || 'Failed to load Google Maps.');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const ensureGeocoder = () => {
        if (!window.google?.maps?.Geocoder) {
            return null;
        }
        if (!geocoderRef.current) {
            geocoderRef.current = new window.google.maps.Geocoder();
        }
        return geocoderRef.current;
    };

    useEffect(() => {
        if (!mapsReady || !autocompleteInputRef.current || !window.google?.maps?.places?.Autocomplete) {
            return;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
            fields: ['formatted_address', 'geometry', 'name', 'place_id'],
            types: ['establishment', 'geocode'],
        });
        autocompleteRef.current = autocomplete;

        const listener = autocomplete.addListener('place_changed', () => {
            const place = autocompleteRef.current?.getPlace();
            if (!place) {
                return;
            }

            const label = place.formatted_address || place.name || '';
            if (label) {
                setSearchValue(label);
            }

            if (place.geometry?.location) {
                const loc = place.geometry.location;
                setLatitude(loc.lat());
                setLongitude(loc.lng());
                return;
            }

            if (place.place_id) {
                const geocoder = ensureGeocoder();
                geocoder?.geocode({ placeId: place.place_id }, (results: any, status: any) => {
                    if (status === 'OK' && results?.[0]?.geometry?.location) {
                        const loc = results[0].geometry.location;
                        setLatitude(loc.lat());
                        setLongitude(loc.lng());
                        if (!label) {
                            setSearchValue(results[0].formatted_address || '');
                        }
                    }
                });
            }
        });

        return () => {
            listener.remove();
            autocompleteRef.current = null;
        };
    }, [mapsReady]);

    useEffect(() => {
        if (!mapsReady || !mapContainerRef.current || !window.google?.maps) {
            return;
        }

        if (!mapInstanceRef.current) {
            mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
                center: DEFAULT_CENTER,
                zoom: 12,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            });
        }

        const latValue = latitude === '' ? DEFAULT_CENTER.lat : Number(latitude);
        const lngValue = longitude === '' ? DEFAULT_CENTER.lng : Number(longitude);
        const position = { lat: latValue, lng: lngValue };

        if (!markerRef.current) {
            markerRef.current = new window.google.maps.Marker({
                position,
                map: mapInstanceRef.current,
            });
        } else {
            markerRef.current.setPosition(position);
        }

        mapInstanceRef.current.setCenter(position);

        if (mapClickListenerRef.current) {
            mapClickListenerRef.current.remove();
        }

    mapClickListenerRef.current = mapInstanceRef.current.addListener('click', (event: any) => {
            if (event.latLng) {
                setLatitude(event.latLng.lat());
                setLongitude(event.latLng.lng());
            }
        });

        return () => {
            if (mapClickListenerRef.current) {
                mapClickListenerRef.current.remove();
                mapClickListenerRef.current = null;
            }
        };
    }, [mapsReady, latitude, longitude]);

    useEffect(() => {
        return () => {
            if (mapClickListenerRef.current) {
                mapClickListenerRef.current.remove();
            }
            if (markerRef.current) {
                markerRef.current.setMap(null);
            }
            mapInstanceRef.current = null;
            markerRef.current = null;
            autocompleteRef.current = null;
        };
    }, []);

    const canUseMaps = mapsReady;
    const mapsWarning = mapsMessage;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await shopAPI.create({
                name,
                latitude: Number(latitude),
                longitude: Number(longitude),
                phone,
                product_type: productType,
                cr_number: crNumber,
            });
            onAdd();
        } catch (error: any) {
            setError(error.message || 'Error adding store');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="store-card manual-store-card">
            <div className="manual-store-header">
                <div className="store-info">
                    <div className="store-name manual-store-title">Manual Add Store</div>
                </div>
            </div>
            {mapsWarning && <div className="manual-store-banner">{mapsWarning}</div>}
            <form onSubmit={handleSubmit} className="manual-store-form">
                <input
                    className="form-input"
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                />
                {canUseMaps && (
                    <input
                        ref={autocompleteInputRef}
                        className="form-input"
                        type="text"
                        placeholder="Search location..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                )}
                {!canUseMaps && (
                    <input
                        className="form-input"
                        type="text"
                        placeholder="Search location..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        disabled
                    />
                )}
                <input
                    className="form-input"
                    type="number"
                    placeholder="Latitude"
                    value={latitude}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        setLatitude(val === '' ? '' : Number(val));
                    }}
                    required
                />
                <input
                    className="form-input"
                    type="number"
                    placeholder="Longitude"
                    value={longitude}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        setLongitude(val === '' ? '' : Number(val));
                    }}
                    required
                />
                {canUseMaps && (
                    <div className="manual-store-map-wrapper">
                        <div className="manual-store-map">
                            <div ref={mapContainerRef} className="manual-store-map-container" />
                        </div>
                        <div className="manual-store-map-hint">Tap on the map to pick a location</div>
                    </div>
                )}
                <input
                    className="form-input"
                    type="text"
                    placeholder="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                />
                <input
                    className="form-input"
                    type="text"
                    placeholder="Product Type"
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    required
                />
                <input
                    className="form-input"
                    type="text"
                    placeholder="CR Number"
                    value={crNumber}
                    onChange={(e) => setCrNumber(e.target.value)}
                    required
                />
                <button
                    type="submit"
                    className="manual-store-submit"
                    disabled={loading}
                >
                    {loading ? 'Adding...' : 'Add Store'}
                </button>
                {error && <div className="manual-store-error">{error}</div>}
            </form>
        </div>
    );
};
